// Módulo Inventário de Estoque
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import inventarioService from "../../services/inventarioService";
import estoqueService from "../../services/estoqueService";
import contasContabeisService from "../../services/contasContabeisService";
import Banner from "../../components/ui/Banner";
import SaldoDuplo from "../../components/estoque/SaldoDuplo";

// ── helpers ──────────────────────────────────────────────────────────────────
const fmtQtd = (v, d = 3) =>
  Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: d });
const fmtMoeda = (v) =>
  Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtD = (d) => {
  try {
    const s = (d || "").split("T")[0].split(" ")[0];
    return s ? new Date(`${s}T12:00:00`).toLocaleDateString("pt-BR") : "—";
  } catch { return d || "—"; }
};
const today = () => new Date().toISOString().slice(0, 10);

const STATUS_BADGE = {
  Rascunho: "warning",
  Aplicado:  "success",
  Cancelado: "secondary",
};
const STATUS_LABEL = { Rascunho: "Rascunho", Aplicado: "Aplicado", Cancelado: "Cancelado" };

const INV_EMPTY = {
  tpinventario: "Inventario",
  dtinventario: today(),
  idlocalpadrao: "",
  idctentrada: "",
  idctsaida: "",
  atualizarcusto: false,
  descricao: "",
  observacao: "",
};

/** Contas analíticas ativas por finalidade contábil do inventário */
const TP_CONTA_ESTOQUE = ["AtivoCirculante", "AtivoNaoCirculante", "Outro"];
const TP_CONTA_GANHO = ["ReceitaOrdinaria", "ReceitaExtraordinaria", "Outro"];
const TP_CONTA_PERDA = ["DespesaAdministrativa", "DespesaOperacional", "DespesaFinanceira", "Outro"];

const contaLabel = (c) => {
  const cod = c.classificacaoContabil || c.classificacaocontabil || "";
  const nom = c.descConta || c.descconta || "";
  return cod ? `${cod} — ${nom}` : nom || `Conta #${c.id}`;
};

const contasParaSelect = (contas, tpContas, selectedId) => {
  const filtradas = contas.filter((c) => tpContas.includes(c.tpConta));
  const sel = selectedId ? String(selectedId) : "";
  if (sel && !filtradas.some((c) => String(c.id) === sel)) {
    const extra = contas.find((c) => String(c.id) === sel);
    if (extra) return [extra, ...filtradas];
  }
  return filtradas;
};

const itemIdUmBase = (row) => row?.idunidadebase || row?.idUnidadeBase || "";
const itemIdUmOp = (row) => row?.idunidadeoperacional || row?.idUnidadeOperacional || "";
const siglaBase = (row) => row?.siglaunidadebase || row?.siglaUnidadeBase || row?.unmedida || "";
const umPadraoLinha = (row) => itemIdUmOp(row) || itemIdUmBase(row) || "";

const unidadesOperacionais = (lista, idBase) => {
  if (!idBase) return lista || [];
  return (lista || []).filter(
    (u) => u.ativo !== 0 && (String(u.idUnidadeBase) === String(idBase) || String(u.id) === String(idBase))
  );
};

const calcQtdBase = (qtd, um) => {
  if (!um || qtd === "" || qtd == null) return null;
  const n = parseFloat(qtd);
  if (!Number.isFinite(n)) return null;
  return n * (um.fatorParaBase || 1);
};

const mapLinhaFromApi = (it, inv) => {
  const idUmInf = it.idunidadeinformada || it.idUnidadeInformada || umPadraoLinha(it) || "";
  const qInf = it.quantidadecontadainformada ?? it.quantidadeContadaInformada;
  const qContada = it.quantidadecontada ?? it.quantidadeContada;
  return {
    _key: it.iditem,
    iditem: it.iditem,
    nmitem: it.nmitem,
    cditem: it.cditem,
    unmedida: it.unmedida,
    idunidadebase: itemIdUmBase(it),
    idunidadeoperacional: itemIdUmOp(it),
    siglaunidadebase: siglaBase(it),
    idlocal: it.idlocal || inv?.idlocalpadrao || "",
    saldosistema: it.saldosistema,
    quantidadecontadainformada: qInf != null && qInf !== "" ? String(qInf) : String(qContada ?? 0),
    idunidadeinformada: idUmInf ? String(idUmInf) : "",
    quantidadecontada: String(qContada ?? 0),
    custounitario: it.custounitario != null ? String(it.custounitario) : "",
  };
};

// ── componente principal ──────────────────────────────────────────────────────
export default function InventarioView() {
  const { condominioAtivo } = useAuth();
  const idCondominio = condominioAtivo?.id;

  const [lista, setLista] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filtroStatus, setFiltroStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [banner, setBanner] = useState(null);

  // Modal inventário
  const [modalAberto, setModalAberto] = useState(false);
  const [invAtual, setInvAtual] = useState(null);  // null = novo
  const [form, setForm] = useState(INV_EMPTY);
  const [salvando, setSalvando] = useState(false);

  // Modal itens (segunda fase: edição de linhas)
  const [modalItens, setModalItens] = useState(false);
  const [invItens, setInvItens] = useState(null);  // inventário carregado com itens
  const [linhas, setLinhas] = useState([]);
  const [picker, setPicker] = useState([]);
  const [pickerSearch, setPickerSearch] = useState("");
  const [locais, setLocais] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [aplicando, setAplicando] = useState(false);
  const [lancamentos, setLancamentos] = useState([]);

  // Modal config
  const [modalConfig, setModalConfig] = useState(false);
  const [cfg, setCfg] = useState({ idctentrada: "", idctsaida: "", idctestoque: "" });
  const [contasContabeis, setContasContabeis] = useState([]);
  const [salvandoCfg, setSalvandoCfg] = useState(false);

  const limite = 15;

  // ── carga lista ─────────────────────────────────────────────────────────────
  const carregar = useCallback(async () => {
    if (!idCondominio) return;
    setLoading(true);
    try {
      const r = await inventarioService.listar({
        idCondominio, status: filtroStatus, page, limit: limite,
      });
      setLista(r.data || []);
      setTotal(r.total || 0);
    } catch (e) {
      setBanner({ type: "danger", msg: e.message || "Erro ao carregar inventários." });
    } finally { setLoading(false); }
  }, [idCondominio, filtroStatus, page]);

  useEffect(() => { carregar(); }, [carregar]);

  // ── locais e contas contábeis (para os modais) ───────────────────────────────
  useEffect(() => {
    if (!idCondominio) return;
    estoqueService.listarLocais({ idCondominio }).then((r) => setLocais(r.data || [])).catch(() => {});
    estoqueService.listarUnidadesMedida({ idCondominio }).then((r) => setUnidades(r.data || [])).catch(() => {});
    contasContabeisService
      .listar({ idCondominio, situacao: "ativo", natureza: "Analitica" })
      .then((r) => setContasContabeis(r.data || []))
      .catch(() => setContasContabeis([]));
  }, [idCondominio]);

  // ── Modal config ─────────────────────────────────────────────────────────────
  const abrirConfig = async () => {
    try {
      const r = await inventarioService.getConfig(idCondominio);
      const c = r.config || {};
      setCfg({
        idctentrada: c.idctentrada || "",
        idctsaida: c.idctsaida || "",
        idctestoque: c.idctestoque || "",
      });
    } catch { setCfg({ idctentrada: "", idctsaida: "", idctestoque: "" }); }
    setModalConfig(true);
  };

  const salvarConfig = async () => {
    setSalvandoCfg(true);
    try {
      await inventarioService.salvarConfig({ idCondominio, ...cfg });
      setBanner({ type: "success", msg: "Configuração salva." });
      setModalConfig(false);
    } catch (e) {
      setBanner({ type: "danger", msg: e.message || "Erro ao salvar configuração." });
    } finally { setSalvandoCfg(false); }
  };

  // ── Modal cabeçalho ──────────────────────────────────────────────────────────
  const novoInventario = () => {
    setInvAtual(null);
    setForm(INV_EMPTY);
    setModalAberto(true);
  };

  const editarInventario = async (inv) => {
    setInvAtual(inv);
    setForm({
      tpinventario: inv.tpinventario,
      dtinventario: (inv.dtinventario || "").split("T")[0].split(" ")[0],
      idlocalpadrao: inv.idlocalpadrao || "",
      idctentrada: inv.idctentrada || "",
      idctsaida: inv.idctsaida || "",
      atualizarcusto: inv.atualizarcusto === 1 || inv.atualizarcusto === true,
      descricao: inv.descricao || "",
      observacao: inv.observacao || "",
    });
    setModalAberto(true);
  };

  const salvarCabecalho = async () => {
    setSalvando(true);
    try {
      if (invAtual) {
        await inventarioService.atualizar(invAtual.id, { ...form, atualizarcusto: form.atualizarcusto ? 1 : 0 });
        setBanner({ type: "success", msg: "Inventário atualizado." });
      } else {
        const r = await inventarioService.criar({ idCondominio, ...form, atualizarcusto: form.atualizarcusto ? 1 : 0 });
        setBanner({ type: "success", msg: "Inventário criado. Agora adicione os itens." });
        setModalAberto(false);
        // Abre diretamente o modal de itens
        await abrirItens(r.id);
        await carregar();
        return;
      }
      setModalAberto(false);
      await carregar();
    } catch (e) {
      setBanner({ type: "danger", msg: e.message || "Erro ao salvar." });
    } finally { setSalvando(false); }
  };

  // ── Modal itens ──────────────────────────────────────────────────────────────
  const abrirItens = async (id) => {
    try {
      const r = await inventarioService.buscar(id);
      setInvItens(r.inventario);
      setLancamentos(r.lancamentos || []);
      const existentes = (r.itens || []).map((it) => mapLinhaFromApi(it, r.inventario));
      setLinhas(existentes);
      setPickerSearch("");
      // Carrega picker
      const p = await inventarioService.itensPicker({
        idCondominio: r.inventario.idcondominio,
        idlocal: r.inventario.idlocalpadrao || "",
      });
      setPicker(p.data || []);
      setModalItens(true);
    } catch (e) {
      setBanner({ type: "danger", msg: e.message || "Erro ao carregar itens." });
    }
  };

  const adicionarItem = (item) => {
    if (linhas.find((l) => l.iditem === item.id)) return;
    const idUm = umPadraoLinha(item);
    setLinhas((prev) => [...prev, {
      _key: item.id,
      iditem: item.id,
      nmitem: item.nmitem,
      cditem: item.cditem,
      unmedida: item.unmedida,
      idunidadebase: itemIdUmBase(item),
      idunidadeoperacional: itemIdUmOp(item),
      siglaunidadebase: siglaBase(item),
      idlocal: invItens?.idlocalpadrao || "",
      saldosistema: item.saldoatual,
      quantidadecontadainformada: String(item.saldoatual || 0),
      idunidadeinformada: idUm ? String(idUm) : "",
      quantidadecontada: String(item.saldoatual || 0),
      custounitario: item.customedio ? String(item.customedio) : "",
    }]);
  };

  const removerLinha = (iditem) => setLinhas((prev) => prev.filter((l) => l.iditem !== iditem));

  const atualizarLinha = (iditem, campo, valor) => {
    setLinhas((prev) =>
      prev.map((l) => (l.iditem === iditem ? { ...l, [campo]: valor } : l))
    );
  };

  const salvarItens = async () => {
    setSalvando(true);
    try {
      const payload = linhas.map((l) => ({
        iditem: l.iditem,
        idlocal: l.idlocal || null,
        quantidadeContadaInformada: parseFloat(l.quantidadecontadainformada) || 0,
        idUnidadeInformada: l.idunidadeinformada || undefined,
        custounitario: l.custounitario !== "" ? parseFloat(l.custounitario) : null,
      }));
      await inventarioService.salvarItens(invItens.id, payload);
      // Recarrega itens para mostrar saldos calculados
      const r = await inventarioService.buscar(invItens.id);
      setInvItens(r.inventario);
      const atualizadas = (r.itens || []).map((it) => mapLinhaFromApi(it, r.inventario));
      setLinhas(atualizadas);
      setBanner({ type: "success", msg: "Itens salvos." });
    } catch (e) {
      setBanner({ type: "danger", msg: e.message || "Erro ao salvar itens." });
    } finally { setSalvando(false); }
  };

  const aplicarInventario = async () => {
    if (!window.confirm("Confirma a aplicação deste inventário? Os saldos serão ajustados e a ação não pode ser desfeita.")) return;
    setAplicando(true);
    try {
      await salvarItens();
      const r = await inventarioService.aplicar(invItens.id, { gerarContabil: true });
      setBanner({
        type: "success",
        msg: r.message || "Inventário aplicado! Saldos e lançamentos contábeis atualizados.",
      });
      setModalItens(false);
      await carregar();
    } catch (e) {
      setBanner({ type: "danger", msg: e.message || "Erro ao aplicar inventário." });
    } finally { setAplicando(false); }
  };

  const cancelarInventario = async (id) => {
    if (!window.confirm("Cancelar este inventário? Esta ação não pode ser desfeita.")) return;
    try {
      await inventarioService.cancelar(id);
      setBanner({ type: "success", msg: "Inventário cancelado." });
      await carregar();
    } catch (e) {
      setBanner({ type: "danger", msg: e.message || "Erro ao cancelar." });
    }
  };

  // ── delta colorido ───────────────────────────────────────────────────────────
  const deltaCor = (delta) => {
    const d = parseFloat(delta || 0);
    if (d > 0) return "text-success";
    if (d < 0) return "text-danger";
    return "text-muted";
  };

  const pickerFiltrado = picker.filter((p) =>
    !pickerSearch ||
    p.nmitem.toLowerCase().includes(pickerSearch.toLowerCase()) ||
    (p.cditem || "").toLowerCase().includes(pickerSearch.toLowerCase())
  );

  const totalPaginas = Math.ceil(total / limite);

  // ── render ───────────────────────────────────────────────────────────────────
  return (
    <div className="container-fluid py-3">
      {banner && (
        <Banner type={banner.type} onClose={() => setBanner(null)}>
          {banner.msg}
        </Banner>
      )}

      {/* Cabeçalho */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h5 className="mb-0 fw-semibold">
          <i className="bi bi-clipboard-check me-2 text-primary" />
          Inventário de Estoque
        </h5>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary btn-sm" onClick={abrirConfig}>
            <i className="bi bi-gear me-1" />Configurar Contas
          </button>
          <button className="btn btn-primary btn-sm" onClick={novoInventario}>
            <i className="bi bi-plus-lg me-1" />Novo Inventário
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body py-2 d-flex gap-2 flex-wrap align-items-center">
          <select
            className="form-select form-select-sm w-auto"
            value={filtroStatus}
            onChange={(e) => { setFiltroStatus(e.target.value); setPage(1); }}
          >
            <option value="">Todos os status</option>
            <option value="Rascunho">Rascunho</option>
            <option value="Aplicado">Aplicado</option>
            <option value="Cancelado">Cancelado</option>
          </select>
          <button className="btn btn-outline-secondary btn-sm ms-auto" onClick={carregar}>
            <i className="bi bi-arrow-clockwise" />
          </button>
        </div>
      </div>

      {/* Tabela */}
      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover table-sm mb-0">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Tipo</th>
                  <th>Data</th>
                  <th>Descrição</th>
                  <th className="text-center">Itens</th>
                  <th>Status</th>
                  <th>Usuário</th>
                  <th className="text-end">Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={8} className="text-center py-4 text-muted">Carregando...</td></tr>
                )}
                {!loading && lista.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-4 text-muted">Nenhum inventário encontrado.</td></tr>
                )}
                {lista.map((inv) => (
                  <tr key={inv.id}>
                    <td className="text-muted small">{inv.id}</td>
                    <td>
                      <span className={`badge bg-${inv.tpinventario === "PrimeiroEstoque" ? "info" : "primary"} bg-opacity-10 text-${inv.tpinventario === "PrimeiroEstoque" ? "info" : "primary"}`}>
                        {inv.tpinventario === "PrimeiroEstoque" ? "Primeiro Estoque" : "Inventário"}
                      </span>
                    </td>
                    <td>{fmtD(inv.dtinventario)}</td>
                    <td className="text-truncate" style={{ maxWidth: 200 }}>{inv.descricao || "—"}</td>
                    <td className="text-center">{inv.qtditens || 0}</td>
                    <td>
                      <span className={`badge bg-${STATUS_BADGE[inv.status] || "secondary"}`}>
                        {STATUS_LABEL[inv.status] || inv.status}
                      </span>
                    </td>
                    <td className="small text-muted">{inv.nmusuario || "—"}</td>
                    <td className="text-end">
                      <div className="d-flex gap-1 justify-content-end">
                        <button
                          className="btn btn-outline-primary btn-sm"
                          title="Ver/editar itens"
                          onClick={() => abrirItens(inv.id)}
                        >
                          <i className="bi bi-list-ul" />
                        </button>
                        {inv.status === "Rascunho" && (
                          <>
                            <button
                              className="btn btn-outline-secondary btn-sm"
                              title="Editar cabeçalho"
                              onClick={() => editarInventario(inv)}
                            >
                              <i className="bi bi-pencil" />
                            </button>
                            <button
                              className="btn btn-outline-danger btn-sm"
                              title="Cancelar"
                              onClick={() => cancelarInventario(inv.id)}
                            >
                              <i className="bi bi-x-circle" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Paginação */}
          {totalPaginas > 1 && (
            <div className="d-flex justify-content-center py-2 gap-1">
              <button className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>‹</button>
              <span className="btn btn-sm disabled">{page}/{totalPaginas}</span>
              <button className="btn btn-sm btn-outline-secondary" disabled={page >= totalPaginas} onClick={() => setPage(page + 1)}>›</button>
            </div>
          )}
        </div>
      </div>

      {/* ── Modal Cabeçalho ────────────────────────────────────────────────────── */}
      {modalAberto && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,.4)" }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-clipboard-check me-2" />
                  {invAtual ? "Editar Inventário" : "Novo Inventário"}
                </h5>
                <button className="btn-close" onClick={() => setModalAberto(false)} />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-sm-4">
                    <label className="form-label fw-semibold">Tipo</label>
                    <select
                      className="form-select"
                      value={form.tpinventario}
                      onChange={(e) => setForm({ ...form, tpinventario: e.target.value })}
                    >
                      <option value="Inventario">Inventário</option>
                      <option value="PrimeiroEstoque">Primeiro Estoque</option>
                    </select>
                  </div>
                  <div className="col-sm-4">
                    <label className="form-label fw-semibold">Data</label>
                    <input
                      type="date"
                      className="form-control"
                      value={form.dtinventario}
                      onChange={(e) => setForm({ ...form, dtinventario: e.target.value })}
                    />
                  </div>
                  <div className="col-sm-4">
                    <label className="form-label fw-semibold">Local padrão</label>
                    <select
                      className="form-select"
                      value={form.idlocalpadrao}
                      onChange={(e) => setForm({ ...form, idlocalpadrao: e.target.value })}
                    >
                      <option value="">— Sem local padrão —</option>
                      {locais.map((l) => (
                        <option key={l.id} value={l.id}>{l.nmLocal || l.nmlocal}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-semibold">Descrição</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Ex.: Inventário junho/2026"
                      value={form.descricao}
                      onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                    />
                  </div>
                  {/* Contas contábeis */}
                  <div className="col-sm-6">
                    <label className="form-label fw-semibold">Conta contrapartida — Ganho (entrada)</label>
                    <select
                      className="form-select"
                      value={form.idctentrada}
                      onChange={(e) => setForm({ ...form, idctentrada: e.target.value })}
                    >
                      <option value="">— Usar padrão da configuração —</option>
                      {contasParaSelect(contasContabeis, TP_CONTA_GANHO, form.idctentrada).map((c) => (
                        <option key={c.id} value={c.id}>{contaLabel(c)}</option>
                      ))}
                    </select>
                    {!contasParaSelect(contasContabeis, TP_CONTA_GANHO).length && (
                      <div className="form-text text-warning">Nenhuma conta de receita analítica ativa.</div>
                    )}
                  </div>
                  <div className="col-sm-6">
                    <label className="form-label fw-semibold">Conta contrapartida — Perda (saída)</label>
                    <select
                      className="form-select"
                      value={form.idctsaida}
                      onChange={(e) => setForm({ ...form, idctsaida: e.target.value })}
                    >
                      <option value="">— Usar padrão da configuração —</option>
                      {contasParaSelect(contasContabeis, TP_CONTA_PERDA, form.idctsaida).map((c) => (
                        <option key={c.id} value={c.id}>{contaLabel(c)}</option>
                      ))}
                    </select>
                    {!contasParaSelect(contasContabeis, TP_CONTA_PERDA).length && (
                      <div className="form-text text-warning">Nenhuma conta de despesa analítica ativa.</div>
                    )}
                  </div>
                  {/* Atualizar custo */}
                  <div className="col-12">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="atualizarcusto"
                        checked={form.atualizarcusto}
                        onChange={(e) => setForm({ ...form, atualizarcusto: e.target.checked })}
                      />
                      <label className="form-check-label" htmlFor="atualizarcusto">
                        Atualizar custo médio ponderado ao aplicar
                        <small className="text-muted ms-2">(usa o custo informado nas linhas)</small>
                      </label>
                    </div>
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-semibold">Observação</label>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={form.observacao}
                      onChange={(e) => setForm({ ...form, observacao: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setModalAberto(false)}>Fechar</button>
                <button className="btn btn-primary" onClick={salvarCabecalho} disabled={salvando}>
                  {salvando ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-check-lg me-1" />}
                  {invAtual ? "Salvar" : "Criar e adicionar itens"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Itens ────────────────────────────────────────────────────────── */}
      {modalItens && invItens && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,.4)" }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title mb-0">
                    <i className="bi bi-clipboard-check me-2" />
                    {invItens.tpinventario === "PrimeiroEstoque" ? "Primeiro Estoque" : "Inventário"} #{invItens.id}
                    <span className={`badge bg-${STATUS_BADGE[invItens.status] || "secondary"} ms-2`}>
                      {STATUS_LABEL[invItens.status]}
                    </span>
                  </h5>
                  <small className="text-muted">{invItens.descricao || ""} · {fmtD(invItens.dtinventario)}</small>
                  {invItens.atualizarcusto === 1 && (
                    <span className="badge bg-info bg-opacity-10 text-info ms-2 small">Atualiza custo médio</span>
                  )}
                </div>
                <button className="btn-close" onClick={async () => { setModalItens(false); await carregar(); }} />
              </div>
              <div className="modal-body p-0">
                <div className="row g-0" style={{ minHeight: 420 }}>
                  {/* Coluna esquerda: picker de itens */}
                  {invItens.status === "Rascunho" && (
                    <div className="col-md-3 border-end p-3" style={{ maxHeight: 520, overflowY: "auto" }}>
                      <p className="small fw-semibold mb-2">
                        <i className="bi bi-search me-1" />Adicionar item
                      </p>
                      <input
                        type="text"
                        className="form-control form-control-sm mb-2"
                        placeholder="Buscar..."
                        value={pickerSearch}
                        onChange={(e) => setPickerSearch(e.target.value)}
                      />
                      {pickerFiltrado.slice(0, 60).map((p) => (
                        <button
                          key={p.id}
                          className="btn btn-outline-secondary btn-sm w-100 text-start mb-1"
                          style={{ fontSize: "0.78rem" }}
                          onClick={() => adicionarItem(p)}
                          disabled={!!linhas.find((l) => l.iditem === p.id)}
                        >
                          <span className="text-muted me-1">{p.cditem}</span> {p.nmitem}
                          <br />
                          <small className="text-muted">
                            Saldo:{" "}
                            <SaldoDuplo
                              qtd={p.saldoatual}
                              unidades={unidades}
                              idUmBase={p.idunidadebase || p.idUnidadeBase}
                              idUmOp={p.idunidadeoperacional || p.idUnidadeOperacional}
                            />
                          </small>
                        </button>
                      ))}
                      {pickerFiltrado.length === 0 && (
                        <p className="text-muted small">Nenhum item.</p>
                      )}
                    </div>
                  )}

                  {/* Coluna direita: tabela de linhas */}
                  <div className={invItens.status === "Rascunho" ? "col-md-9 p-3" : "col-12 p-3"}>
                    {linhas.length === 0 ? (
                      <p className="text-muted text-center py-4">
                        {invItens.status === "Rascunho"
                          ? "Adicione itens pela lista à esquerda."
                          : "Nenhum item neste inventário."}
                      </p>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-sm table-hover mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Cód.</th>
                              <th>Item</th>
                              <th>UN</th>
                              <th className="text-end">Saldo sistema</th>
                              <th style={{ minWidth: 72 }}>UM</th>
                              <th className="text-end" style={{ minWidth: 110 }}>Qtd. contada</th>
                              <th className="text-end">Delta (base)</th>
                              {invItens.atualizarcusto === 1 && (
                                <th className="text-end" style={{ minWidth: 120 }}>Custo unit.</th>
                              )}
                              <th className="text-end">Valor ajuste</th>
                              {invItens.status === "Rascunho" && <th />}
                            </tr>
                          </thead>
                          <tbody>
                            {linhas.map((l) => {
                              const umsItem = unidadesOperacionais(unidades, l.idunidadebase || l.idunidadeoperacional);
                              const umSel = unidades.find((u) => String(u.id) === String(l.idunidadeinformada));
                              const qBase = calcQtdBase(l.quantidadecontadainformada, umSel)
                                ?? (parseFloat(l.quantidadecontada) || 0);
                              const delta = qBase - (parseFloat(l.saldosistema) || 0);
                              const custo = parseFloat(l.custounitario) || 0;
                              const valorAjuste = Math.abs(delta) * custo;
                              const siglaB = l.siglaunidadebase || l.unmedida || "base";
                              return (
                                <tr key={l.iditem}>
                                  <td className="text-muted small">{l.cditem}</td>
                                  <td>{l.nmitem}</td>
                                  <td className="text-muted small">{siglaB}</td>
                                  <td className="text-end">{fmtQtd(l.saldosistema)} {siglaB}</td>
                                  <td>
                                    {invItens.status === "Rascunho" && umsItem.length > 0 ? (
                                      <select
                                        className="form-select form-select-sm"
                                        style={{ width: 72 }}
                                        value={l.idunidadeinformada || ""}
                                        onChange={(e) => atualizarLinha(l.iditem, "idunidadeinformada", e.target.value)}
                                      >
                                        {umsItem.map((u) => (
                                          <option key={u.id} value={u.id}>{u.sigla}</option>
                                        ))}
                                      </select>
                                    ) : (
                                      <span className="text-muted small">{umSel?.sigla || siglaB}</span>
                                    )}
                                  </td>
                                  <td className="text-end">
                                    {invItens.status === "Rascunho" ? (
                                      <div>
                                        <input
                                          type="number"
                                          className="form-control form-control-sm text-end"
                                          style={{ width: 90, marginLeft: "auto" }}
                                          value={l.quantidadecontadainformada}
                                          onChange={(e) => atualizarLinha(l.iditem, "quantidadecontadainformada", e.target.value)}
                                          min="0"
                                          step="0.001"
                                        />
                                        {umSel && calcQtdBase(l.quantidadecontadainformada, umSel) != null && (
                                          <small className="text-muted d-block">
                                            = {fmtQtd(calcQtdBase(l.quantidadecontadainformada, umSel))} {siglaB}
                                          </small>
                                        )}
                                      </div>
                                    ) : (
                                      <span>
                                        {fmtQtd(l.quantidadecontadainformada ?? l.quantidadecontada)}
                                        {umSel?.sigla ? ` ${umSel.sigla}` : ""}
                                        <br />
                                        <small className="text-muted">({fmtQtd(l.quantidadecontada)} {siglaB})</small>
                                      </span>
                                    )}
                                  </td>
                                  <td className={`text-end fw-semibold ${deltaCor(delta)}`}>
                                    {delta > 0 ? "+" : ""}{fmtQtd(delta)}
                                  </td>
                                  {invItens.atualizarcusto === 1 && (
                                    <td className="text-end">
                                      {invItens.status === "Rascunho" ? (
                                        <input
                                          type="number"
                                          className="form-control form-control-sm text-end"
                                          style={{ width: 110 }}
                                          value={l.custounitario}
                                          onChange={(e) => atualizarLinha(l.iditem, "custounitario", e.target.value)}
                                          min="0"
                                          step="0.0001"
                                          placeholder="0,0000"
                                        />
                                      ) : (
                                        l.custounitario ? fmtMoeda(l.custounitario) : "—"
                                      )}
                                    </td>
                                  )}
                                  <td className="text-end small text-muted">
                                    {custo > 0 ? fmtMoeda(valorAjuste) : "—"}
                                  </td>
                                  {invItens.status === "Rascunho" && (
                                    <td className="text-center">
                                      <button
                                        className="btn btn-link btn-sm text-danger p-0"
                                        onClick={() => removerLinha(l.iditem)}
                                        title="Remover"
                                      >
                                        <i className="bi bi-trash" />
                                      </button>
                                    </td>
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {invItens.status === "Aplicado" && lancamentos.length > 0 && (
                <div className="border-top px-3 py-2">
                  <h6 className="small fw-semibold mb-2">Lançamentos contábeis</h6>
                  <div className="table-responsive">
                    <table className="table table-sm mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Histórico</th>
                          <th>Débito</th>
                          <th>Crédito</th>
                          <th className="text-end">Valor</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lancamentos.map((lc) => (
                          <tr key={lc.id}>
                            <td className="small">{lc.historico}</td>
                            <td className="small">{lc.nmcontadebito || lc.nmContaDebito}</td>
                            <td className="small">{lc.nmcontacredito || lc.nmContaCredito}</td>
                            <td className="text-end">{fmtMoeda(lc.valor)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              <div className="modal-footer d-flex justify-content-between">
                <div className="text-muted small">
                  {linhas.length} {linhas.length === 1 ? "item" : "itens"}
                </div>
                <div className="d-flex gap-2">
                  <button className="btn btn-secondary" onClick={async () => { setModalItens(false); await carregar(); }}>
                    Fechar
                  </button>
                  {invItens.status === "Rascunho" && (
                    <>
                      <button className="btn btn-outline-primary" onClick={salvarItens} disabled={salvando || linhas.length === 0}>
                        {salvando ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-floppy me-1" />}
                        Salvar rascunho
                      </button>
                      <button
                        className="btn btn-success"
                        onClick={aplicarInventario}
                        disabled={aplicando || linhas.length === 0}
                      >
                        {aplicando ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-check2-all me-1" />}
                        Aplicar inventário
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Config ───────────────────────────────────────────────────────── */}
      {modalConfig && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,.4)" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-gear me-2" />Configuração Contábil — Inventário
                </h5>
                <button className="btn-close" onClick={() => setModalConfig(false)} />
              </div>
              <div className="modal-body">
                <p className="text-muted small mb-3">
                  Defina as contas padrão usadas nos lançamentos de ajuste de inventário.
                  Cada inventário pode sobrescrever estas contas individualmente.
                </p>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Contrapartida — Ganho / Entrada (sobra)</label>
                  <select
                    className="form-select"
                    value={cfg.idctentrada}
                    onChange={(e) => setCfg({ ...cfg, idctentrada: e.target.value })}
                  >
                    <option value="">— Não configurado —</option>
                    {contasParaSelect(contasContabeis, TP_CONTA_GANHO, cfg.idctentrada).map((c) => (
                      <option key={c.id} value={c.id}>{contaLabel(c)}</option>
                    ))}
                  </select>
                  <div className="form-text">Receitas ordinárias, extraordinárias ou “Outro”.</div>
                  {!contasParaSelect(contasContabeis, TP_CONTA_GANHO).length && (
                    <div className="form-text text-warning">Nenhuma conta de receita analítica ativa no plano.</div>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Contrapartida — Perda / Saída (falta)</label>
                  <select
                    className="form-select"
                    value={cfg.idctsaida}
                    onChange={(e) => setCfg({ ...cfg, idctsaida: e.target.value })}
                  >
                    <option value="">— Não configurado —</option>
                    {contasParaSelect(contasContabeis, TP_CONTA_PERDA, cfg.idctsaida).map((c) => (
                      <option key={c.id} value={c.id}>{contaLabel(c)}</option>
                    ))}
                  </select>
                  <div className="form-text">Despesas administrativas, operacionais, financeiras ou “Outro”.</div>
                  {!contasParaSelect(contasContabeis, TP_CONTA_PERDA).length && (
                    <div className="form-text text-warning">Nenhuma conta de despesa analítica ativa no plano.</div>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Conta de ativo — Estoque</label>
                  <select
                    className="form-select"
                    value={cfg.idctestoque}
                    onChange={(e) => setCfg({ ...cfg, idctestoque: e.target.value })}
                  >
                    <option value="">— Não configurado —</option>
                    {contasParaSelect(contasContabeis, TP_CONTA_ESTOQUE, cfg.idctestoque).map((c) => (
                      <option key={c.id} value={c.id}>{contaLabel(c)}</option>
                    ))}
                  </select>
                  <div className="form-text">Ativo circulante, não circulante ou “Outro”.</div>
                  {!contasParaSelect(contasContabeis, TP_CONTA_ESTOQUE).length && (
                    <div className="form-text text-warning">Nenhuma conta de ativo analítica ativa no plano.</div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setModalConfig(false)}>Cancelar</button>
                <button className="btn btn-primary" onClick={salvarConfig} disabled={salvandoCfg}>
                  {salvandoCfg ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-check-lg me-1" />}
                  Salvar configuração
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
